/**
 * IconButton — icon-only button with a mandatory accessible label.
 *
 * The `label` prop is required and rendered as `aria-label`; icon-only controls
 * must never rely on the glyph alone for meaning (WCAG 4.1.2).
 */
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import styles from './IconButton.module.css';

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Required accessible name; describes the action, not the icon. */
  label: string;
  /** Icon glyph or SVG content. */
  children: ReactNode;
  /** Optional tooltip text shown on hover; defaults to `label`. */
  tooltip?: string;
}

/**
 * Render a square icon button. Forwards native props and enforces an
 * accessible name via the required `label`.
 */
export function IconButton({
  label,
  children,
  tooltip,
  disabled,
  className,
  type = 'button',
  ...rest
}: IconButtonProps) {
  const classes = [styles.iconBtn, className ?? ''].filter(Boolean).join(' ');
  return (
    <button
      type={type}
      className={classes}
      aria-label={label}
      title={tooltip ?? label}
      disabled={disabled}
      {...rest}
    >
      {children}
    </button>
  );
}
